import React, {
  useState,
  KeyboardEvent,
} from 'react';
import i18n from 'i18next';

import {
  Icon,
  Popup,
  Tooltip,
} from 'web-components';

import {
  SVG,
  icons,
  Flex,
} from 'web-styled';
import config from 'web-config';

import * as S from './styled';

const addIcons = {
  client: SVG.Clients,
  exercise: SVG.Exercise,
  workout: SVG.KettleBell,
  program: SVG.Program,
  video: SVG.VideoLibrary,
  announcement: SVG.Announcement,
};

type Action = {
  icon: string,
  name: string,
  action: { (): void },
};

type Props = {
  isPro: boolean;
  avatar: string;
  namespace: any;
  searchQuery: string;
  addActions: Action[];
  profileActions: Action[];
  setSearchQuery: { (text: string): void };
  onKeyDown: { (event: KeyboardEvent): void };
};

const Header = ({
  isPro,
  avatar,
  addActions,
  profileActions,
  namespace,
  searchQuery,
  setSearchQuery,
  onKeyDown,
}: Props) => {
  const [menuisOpen, setMenuisOpen] = useState(false);
  const [helpisOpen, setHelpisOpen] = useState(false);
  const [addActionsisOpen, setAddActionsisOpen] = useState(false);

  return (
    <S.Header>
      {isPro && (
        <S.Search
          data-tour="tour__search"
        >
          <S.Input
            placeholder="Search your library"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onKeyDown={onKeyDown}
          />
          {
            searchQuery
              ? (
                <S.SearchButton
                  onClick={() => {
                    setSearchQuery('');
                  }}
                >
                  <Icon type="fas fa-times" />
                </S.SearchButton>
              ) : (
                <S.SearchButton>
                  <Icon type="fas fa-search" />
                </S.SearchButton>
              )
          }
        </S.Search>
      )}
      <Flex margin="0 0 0 auto">
        {isPro && (
          <S.Dropdown
            isOpen={addActionsisOpen}
          >
            <Popup
              isOpen={addActionsisOpen}
              trigger={(
                <S.Trigger isOpen={addActionsisOpen}>
                  <S.PlusCircle
                    isOpen={addActionsisOpen}
                  />
                </S.Trigger>
              )}
              setPopupVisible={() => setAddActionsisOpen(!addActionsisOpen)}
              onClickOutside={() => setAddActionsisOpen(false)}
            >
              <S.Actions
                mr={116}
                minWidth="180px"
              >
                {addActions.map((action) => {
                  const IconComponent = addIcons[action.icon];
                  return (
                    <S.Action
                      key={action.name}
                      onClick={() => {
                        action.action();
                        setAddActionsisOpen(false);
                      }}
                      align="center"
                    >
                      <Flex flex="0 0 20px" align="center">
                        <S.SVGIcon>
                          <IconComponent
                            fill="#fff"
                            stroke="#fff"
                          />
                        </S.SVGIcon>
                      </Flex>
                      <span>{action.name}</span>
                    </S.Action>
                  );
                })}
              </S.Actions>
            </Popup>
          </S.Dropdown>
        )}

        <S.Dropdown
          data-tour="tour__help"
          isOpen={helpisOpen}
        >
          <Popup
            isOpen={helpisOpen}
            trigger={(
              <S.Trigger isOpen={helpisOpen}>
                <Tooltip
                  body={i18n.t('tooltip.main.header.help')}
                  isOpen={helpisOpen ? false : undefined}
                  positions={['left']}
                >
                  <Flex>
                    <S.HelpIcon />
                  </Flex>
                </Tooltip>
              </S.Trigger>
            )}
            setPopupVisible={() => setHelpisOpen(!helpisOpen)}
            onClickOutside={() => setHelpisOpen(false)}
          >
            <S.Actions mr={76}>
              <S.Action
                as="a"
                rel="noopener noreferrer"
                href="https://gymcloud.freshdesk.com/support/tickets/new"
                target="_blank"
                align="center"
              >
                <Flex flex="0 0 15px">
                  <Icon
                    size="14px"
                    type="fas fa-pencil-alt"
                  />
                </Flex>
                <span>Give Feedback</span>
              </S.Action>
              <S.Action
                as="a"
                rel="noopener noreferrer"
                href={(isPro || namespace.symbol === 'gymcloud' || namespace.symbol === 'ideafit') ? config.urls.freshdesk : config.urls.support}
                target="_blank"
                align="center"
              >
                <Flex
                  flex="0 0 15px"
                >
                  <img
                    src={icons.helpcenter}
                    alt=""
                    width="13px"
                  />
                </Flex>
                <span>Go to Support Portal</span>
              </S.Action>
            </S.Actions>
          </Popup>
        </S.Dropdown>
        <S.Dropdown
          width="76px"
          data-tour="tour__profile"
          isOpen={menuisOpen}
        >
          <Popup
            isOpen={menuisOpen}
            trigger={(
              <Flex>
                {isPro ? (
                  <Tooltip
                    body={i18n.t('tooltip.main.header.user')}
                    isOpen={menuisOpen ? false : undefined}
                    positions={['left']}
                  >
                    <S.Avatar
                      avatar={avatar || namespace.webapp_logo_mark?.url}
                    />
                  </Tooltip>
                ) : (
                  <S.Avatar
                    avatar={avatar || namespace.webapp_logo_mark?.url}
                  />
                )}
              </Flex>
            )}
            setPopupVisible={() => setMenuisOpen(!menuisOpen)}
            onClickOutside={() => setMenuisOpen(false)}
          >
            <S.Actions>
              {profileActions.map(item => (
                <S.Action
                  key={item.name}
                  onClick={() => {
                    item.action();
                    setMenuisOpen(false);
                  }}
                >
                  <Flex flex="0 0 25%">
                    <S.ActionIcon
                      icon={item.icon}
                      style={{
                        backgroundImage: `url(${icons[item.icon]})`,
                      }}
                    />
                  </Flex>
                  <span>{item.name}</span>
                </S.Action>
              ))}
            </S.Actions>
          </Popup>
        </S.Dropdown>
      </Flex>
    </S.Header>
  );
};

export default Header;
